import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  ViewChild,
  TemplateRef,
  inject,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';
import { Subject, Subscription } from 'rxjs';
import { filter, take, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { TreeNode } from 'primeng/api';
import { TreeTableModule } from 'primeng/treetable';
import { ProceduresService } from '../../../../core/services/procedures.service';
import { Procedure } from '../../../../core/models/procedure.model';
import { selectUserInfo } from '../../../../core/store/auth/auth.selectors';
import { ProcedureDetailsModal } from '../procedure-details-modal/procedure-details-modal';
import { AddProcedureModal } from '../add-procedure-modal/add-procedure-modal';
import { EditProcedureModal } from '../edit-procedure-modal/edit-procedure-modal';
import { MedicalPercentagesModalComponent } from '../medical-percentages-modal/medical-percentages-modal';
import { TranslateModule } from '@ngx-translate/core';
import { PrimeTableColumn } from '../../../../shared/components/prime-table/prime-table';
import { FormsModule } from '@angular/forms';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { AppButton } from '../../../../shared/components/app-button/app-button';
import { AppDropdown } from '../../../../shared/components/dropdown/dropdown';
import { formatNumber } from '../../../../core/utils/number.utils';
import { buildProceduresTree, ProcedureTreeNodeData } from './procedures-tree.util';
import { HasPermissionDirective } from '../../../../shared/directives/has-permission.directive';
import { PermissionService, MODULE } from '../../../../core/services/permission.service';

@Component({
  selector: 'app-clinic-procedures',
  standalone: true,
  imports: [
    CommonModule,
    ProcedureDetailsModal,
    AddProcedureModal,
    EditProcedureModal,
    MedicalPercentagesModalComponent,
    TranslateModule,
    TreeTableModule,
    FormsModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    AppButton,
    AppDropdown,
    HasPermissionDirective,
  ],
  templateUrl: './clinic-procedures.html',
  styles: [
    `
      /* NOTE: selectors are intentionally FLAT (no nesting) — nesting inside
         ':host ::ng-deep {}' gets mangled by lightningcss into broken
         '[_ngcontent]' / empty ':is()' selectors that never match. */
      :host ::ng-deep .procedures-treetable .p-treetable-table {
        border-collapse: collapse;
      }
      /* Only force the wide layout on desktop; mobile collapses columns and fits. */
      @media (min-width: 640px) {
        :host ::ng-deep .procedures-treetable .p-treetable-table {
          min-width: 44rem;
        }
      }
      /* Tighter horizontal padding on small screens */
      @media (max-width: 639px) {
        :host ::ng-deep .procedures-treetable .p-treetable-thead > tr > th,
        :host ::ng-deep .procedures-treetable .p-treetable-tbody > tr > td {
          padding-left: 0.85rem;
          padding-right: 0.85rem;
        }
      }

      /* Header */
      :host ::ng-deep .procedures-treetable .p-treetable-thead > tr > th {
        background: #f8fafc;
        color: #94a3b8;
        font-weight: 800;
        font-size: 0.625rem;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        border: none;
        border-bottom: 1px solid #eef2f6;
        padding: 0.9rem 1.5rem;
        white-space: nowrap;
      }

      /* Base row + cells */
      :host ::ng-deep .procedures-treetable .p-treetable-tbody > tr {
        transition: background-color 0.15s ease;
      }
      :host ::ng-deep .procedures-treetable .p-treetable-tbody > tr > td {
        border: none;
        border-bottom: 1px solid #f4f6f9;
        padding: 0.6rem 1.5rem;
        vertical-align: middle;
      }

      /* Toggler button — matches PrimeNG's own specificity so the size wins */
      :host ::ng-deep .procedures-treetable .p-treetable-tbody > tr > td .p-treetable-toggler {
        width: 1rem;
        height: 1rem;
        margin: 0 0.375rem 0 0;
        color: #94a3b8;
        border-radius: 0.375rem;
        transition: all 0.15s ease;
      }
      :host ::ng-deep .procedures-treetable .p-treetable-tbody > tr > td .p-treetable-toggler:hover {
        background: #eef2f6;
        color: #475569;
      }
      /* The chevron itself (an <svg class="p-icon" width="14" height="14">) */
      :host ::ng-deep .procedures-treetable .p-treetable-toggler svg {
        width: 0.7rem !important;
        height: 0.7rem !important;
      }
      :host ::ng-deep .procedures-treetable tr.leaf-row .p-treetable-toggler {
        visibility: hidden;
      }

      /* The group name is a real button now (it toggles the row), so it needs
         a visible keyboard focus ring. Mouse clicks keep no outline. */
      .group-toggle:focus-visible {
        outline: 2px solid var(--primary, #6366f1);
        outline-offset: 2px;
        border-radius: 0.25rem;
      }

      /* Row backgrounds */
      :host ::ng-deep .procedures-treetable tr.family-row > td {
        background: #f8fafc;
        border-bottom: 1px solid #eef2f6;
      }
      :host ::ng-deep .procedures-treetable tr.group-row:hover > td {
        background: #eef2f6;
      }
      :host ::ng-deep .procedures-treetable tr.leaf-row:hover > td {
        background: #fafbfc;
      }

      /* Loading overlay */
      :host ::ng-deep .procedures-treetable .p-treetable-loading-overlay {
        background: rgba(255, 255, 255, 0.7);
      }
      :host ::ng-deep .procedures-treetable .p-treetable-loading-icon {
        color: var(--primary, #6366f1);
        font-size: 1.75rem;
      }
    `,
  ],
})
export class ClinicProcedures implements OnInit, OnDestroy {
  @ViewChild('statusTemplate', { static: true }) statusTemplate!: TemplateRef<any>;
  @ViewChild('actionsTemplate', { static: true }) actionsTemplate!: TemplateRef<any>;
  @ViewChild('valueTemplate', { static: true }) valueTemplate!: TemplateRef<any>;
  @ViewChild('procedureTypeTemplate', { static: true }) procedureTypeTemplate!: TemplateRef<any>;
  templates: { [key: string]: TemplateRef<any> } = {};

  loading = true;

  columns: PrimeTableColumn[] = [
    { field: 'label', header: 'clinic_procedures_table_hierarchy' },
    {
      field: 'procedure_type',
      header: 'clinic_procedures_table_type',
      type: 'custom',
      width: '160px',
    },
    {
      field: 'base_price',
      header: 'clinic_procedures_table_value',
      type: 'custom',
      width: '160px',
    },
    { field: 'active', header: 'clinic_procedures_table_status', type: 'boolean', width: '140px' },
    {
      field: 'actions',
      header: 'clinic_procedures_table_actions',
      type: 'action',
      width: '100px',
      styleClass: 'text-center',
    },
  ];

  allProcedures: Procedure[] = [];
  treeNodes: TreeNode<ProcedureTreeNodeData>[] = [];
  filteredTreeNodes: TreeNode<ProcedureTreeNodeData>[] = [];
  allExpanded = false;

  searchText = '';
  branchId: number | null = null;
  openMenuId: number | null = null;
  selectedProcedure: any | null = null;
  editingProcedure: any | null = null;
  showAddModal = false;
  showPercentagesModal = false;
  selectedProcedureForPercentages: any = null;

  private searchSubject = new Subject<string>();
  private searchSubscription?: Subscription;

  private route = inject(ActivatedRoute);
  private perm = inject(PermissionService);

  constructor(
    private proceduresService: ProceduresService,
    private store: Store,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.templates = {
      active: this.statusTemplate,
      actions: this.actionsTemplate,
      base_price: this.valueTemplate,
      procedure_type: this.procedureTypeTemplate,
    };

    this.loading = true;
    this.store
      .select(selectUserInfo)
      .pipe(
        filter((info) => !!info),
        take(1)
      )
      .subscribe((userInfo) => {
        // Logic copied from auth.effects.ts to ensure consistency
        this.branchId = userInfo.branches_id || userInfo.branch_id;
        if (this.branchId) {
          // Check for search query param
          const searchParam = this.route.snapshot.queryParamMap.get('search');
          if (searchParam) {
            this.searchText = searchParam;
          }

          this.loadProcedures();
          // Warm the VAT/exemption lists now: the call takes 3-4 s and the
          // Edit procedure modal needs them. Fire-and-forget; the modal reuses
          // this call, or makes its own if this one failed.
          this.proceduresService.getTaxesAndExemptionsCached(this.branchId).catch((err) =>
            console.warn('Prefetching taxes/exemptions failed; the edit modal will retry', err),
          );
        } else {
          console.error('No branch ID found for user');
          this.loading = false;
          this.cdr.detectChanges();
        }
      });

    this.searchSubscription = this.searchSubject
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((text) => {
        this.searchText = text;
        this.applySearchFilter();
      });
  }

  ngOnDestroy() {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
    this.searchSubject.complete();
  }

  async loadProcedures() {
    if (!this.branchId) return;

    this.loading = true;
    this.cdr.detectChanges();

    try {
      const { data, error } = await this.proceduresService.getClinicProceduresTree(this.branchId);

      if (error) throw error;

      this.allProcedures = (data as Procedure[]) || [];
      this.treeNodes = buildProceduresTree(this.allProcedures);
      this.applySearchFilter();
    } catch (e) {
      console.error('Exception fetching procedures', e);
      this.allProcedures = [];
      this.treeNodes = [];
      this.filteredTreeNodes = [];
    } finally {
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  onSearch(text: string) {
    this.searchSubject.next(text);
  }

  applySearchFilter() {
    const term = this.searchText.trim().toLowerCase();
    if (!term) {
      this.filteredTreeNodes = this.treeNodes;
      this.allExpanded = false;
      this.cdr.detectChanges();
      return;
    }
    this.filteredTreeNodes = this.filterTree(this.treeNodes, term);
    this.allExpanded = true;
    this.cdr.detectChanges();
  }

  private filterTree(
    nodes: TreeNode<ProcedureTreeNodeData>[],
    term: string
  ): TreeNode<ProcedureTreeNodeData>[] {
    const result: TreeNode<ProcedureTreeNodeData>[] = [];
    for (const node of nodes) {
      if (node.leaf) {
        const procedure = node.data?.procedure;
        const hit =
          procedure?.description?.toLowerCase().includes(term) ||
          procedure?.internal_code?.toLowerCase().includes(term);
        if (hit) result.push(node);
        continue;
      }
      const filteredChildren = this.filterTree(node.children ?? [], term);
      if (filteredChildren.length > 0) {
        result.push({ ...node, children: filteredChildren, expanded: true });
      }
    }
    return result;
  }

  getTemplate(col: PrimeTableColumn): TemplateRef<any> | null {
    return this.templates[col.field] || null;
  }

  /**
   * Expand/collapse one group from its label.
   *
   * The chevron already does this via p-treeTableToggler, but that is a tiny
   * hit target and users kept clicking the name and getting nothing. Same
   * mechanism as toggleExpandAll(): flip `expanded` and hand p-treetable a new
   * array reference so it re-serialises the rows.
   */
  toggleNode(rowNode: { node: TreeNode<ProcedureTreeNodeData> }) {
    const node = rowNode?.node;
    // Leaves have nothing to open - their row click belongs to the row itself.
    if (!node || node.leaf) return;
    node.expanded = !node.expanded;
    this.filteredTreeNodes = [...this.filteredTreeNodes];
    this.cdr.detectChanges();
  }

  toggleExpandAll() {
    this.allExpanded = !this.allExpanded;
    this.setExpanded(this.filteredTreeNodes, this.allExpanded);
    this.filteredTreeNodes = [...this.filteredTreeNodes];
    this.cdr.detectChanges();
  }

  private setExpanded(nodes: TreeNode<ProcedureTreeNodeData>[], expanded: boolean) {
    for (const node of nodes) {
      if (!node.leaf) {
        node.expanded = expanded;
        this.setExpanded(node.children ?? [], expanded);
      }
    }
  }

  groupTextClass(levelType: string): string {
    switch (levelType) {
      case 'family':
        return 'text-sm font-extrabold text-slate-900 uppercase tracking-wide';
      case 'subfamily':
        return 'text-[13px] font-bold text-slate-700';
      case 'group':
        return 'text-[13px] font-semibold text-slate-600';
      default:
        return 'text-[13px] font-medium text-slate-500';
    }
  }

  toggleMenu(id: number, event: Event) {
    event.stopPropagation();
    if (this.openMenuId === id) {
      this.openMenuId = null;
    } else {
      this.openMenuId = id;
    }
  }

  openDetails(procedure: any) {
    this.selectedProcedure = procedure;
    this.openMenuId = null; // Close menu
  }

  onAddProcedureSuccess(success: boolean) {
    this.showAddModal = false;
    if (success) {
      this.loadProcedures();
    }
  }

  openEdit(procedure: any) {
    this.editingProcedure = procedure;
    this.openMenuId = null;
  }

  onEditSuccess() {
    this.loadProcedures();
  }

  async toggleStatus(procedure: any) {
    this.openMenuId = null;
    if (!this.perm.canEdit(MODULE.PROCEDIMENTOS)) return; // status change requires edit access
    if (!procedure.id) return;

    try {
      const newStatus = !procedure.active;
      const masterId = procedure.procedure_id || procedure.product_id || procedure.id_procedure;
      const updateTasks = [
        this.proceduresService.updateClinicProcedure(procedure.id, { active: newStatus }),
      ];

      if (masterId) {
        updateTasks.push(this.proceduresService.updateProcedure(masterId, { active: newStatus }));
      }

      await Promise.all(updateTasks);

      procedure.active = newStatus;
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error toggling procedure status:', error);
    }
  }

  openMedicalPercentages(procedure: any) {
    this.selectedProcedureForPercentages = procedure;
    this.showPercentagesModal = true;
    this.openMenuId = null;
  }

  formatMoney(value: any): string {
    return formatNumber(Number(value) || 0);
  }
}
